import type { PublishedCollection } from '@/types/schema';
import type { AppDefinition } from '@/types/app';
import type { EntitySchema } from '@/types/schema';
import type { AppRole } from './rules';

export type { AppRole };

/** A user's membership in an app (in-app users). */
export interface AppMember {
  userId: string;
  role: AppRole;
  joinedAt: string;
  /** Last authenticated use of the app (set server-side on each request). */
  lastActiveAt?: string;
  /** The user's email, captured at first use. */
  email?: string;
}

/** A pending invite to join an app, addressed by email or phone. */
export interface AppInvite {
  token: string;
  appId: string;
  role: AppRole;
  email?: string;
  phone?: string;
  invitedBy: string;
  createdAt: string;
  /** Best-effort delivery outcome (email/SMS), present on the invite response. */
  delivery?: { channel: 'email' | 'sms' | 'none'; sent: boolean; error?: string };
}

export interface InviteInput {
  email?: string;
  phone?: string;
  role?: AppRole;
}

export interface BackendRecord {
  id: string;
  createdAt: string;
  updatedAt: string;
  [key: string]: unknown;
}

export interface PaginatedRecords {
  items: BackendRecord[];
  cursor?: string;
  count: number;
  /** High-water mark returned by a ?since query — the max updatedAt in this page. Use as the next sync cursor. */
  syncedAt?: string;
}

export interface AppListing {
  appId: string;
  name: string;
  description: string;
  icon: string;
  category?: string;
  creatorId: string;
  publishedAt: string;
  // A remix-only "clone-me" demo: a published personal app that can be forked but not joined/used.
  // The Discover card shows a Demo badge + a Clone action instead of Get for these.
  remixOnly?: boolean;
  // A personal app: private to its creator, listed only in their My Apps, never in Discover.
  personal?: boolean;
  // Publish-to-web state (the password hash is never sent to clients).
  webPublished?: boolean;
  webVisibility?: 'public' | 'password';
  slug?: string;
  /** The design kit the app chose, so a listing card can wear the app's own colours. */
  kit?: string;
  // Canonical CloudFront site URL, provided by the backend when published — so the
  // client never needs to know the origin or build a broken API-Gateway link.
  siteUrl?: string;
}

export interface PublishInput {
  name: string;
  description: string;
  icon: string;
  category?: string;
  definition: AppDefinition;
  schemas: EntitySchema[];
}

/** Sync metadata carried with write operations so the server can apply LWW logic. */
export interface WriteMeta {
  clientUpdatedAt?: string; // ISO 8601 — the client's edit timestamp
  deviceId?: string;        // stable per-install id for LWW tie-breaking
}

/** App-scoped record operations. Created via the factory for one appId. */
export interface BackendClient {
  listRecords(entity: string, opts?: { limit?: number; cursor?: string; since?: string }): Promise<PaginatedRecords>;
  getRecord(entity: string, id: string): Promise<BackendRecord>;
  createRecord(entity: string, data: Record<string, unknown>, meta?: WriteMeta): Promise<BackendRecord>;
  updateRecord(entity: string, id: string, data: Record<string, unknown>, meta?: WriteMeta): Promise<BackendRecord>;
  deleteRecord(entity: string, id: string, meta?: WriteMeta): Promise<{ deleted: boolean; id: string }>;
}

/** Platform-level catalog + enrollment operations (not app-scoped). */
export interface PlatformBackend {
  publishApp(input: PublishInput): Promise<AppListing>;
  listApps(): Promise<AppListing[]>;
  getApp(appId: string): Promise<AppListing | null>;
  enroll(appId: string): Promise<{ appId: string; enrolled: boolean }>;
  listMyApps(): Promise<AppListing[]>;
  /** Remove a published app from the catalog (creator or platform moderator). */
  unpublish(appId: string): Promise<{ appId: string; unpublished: boolean }>;
  /** Report a published app's content for moderation (App Store Guideline 1.2). */
  reportApp(appId: string, reason: string): Promise<{ reported: boolean }>;
  /** Purge the signed-in user's server data (call before deleting the auth account). */
  deleteAccount(): Promise<{ deleted: number }>;
  /** Collect an email subscriber (server sends the double-opt-in confirmation). */
  subscribe(appId: string, email: string): Promise<{ status: string }>;
  /** Owner-only: broadcast an update to confirmed subscribers. */
  broadcast(appId: string, payload: { subject: string; body: string; senderAddress: string }): Promise<{ recipients: number; sent: number }>;

  // --- In-app users (membership + invites) ---
  /** Invite someone to an app by email or phone (owner/editor only). Returns the
   *  invite incl. a token; delivery (SES/SNS) is a separate concern. */
  inviteToApp(appId: string, input: InviteInput): Promise<AppInvite>;
  /** Accept an invite token → join the app with the invite's role. */
  acceptInvite(token: string): Promise<{ appId: string; role: AppRole }>;
  /** List an app's members (owner/editor only). */
  listMembers(appId: string): Promise<AppMember[]>;
  /** Change a member's role (owner only). */
  setMemberRole(appId: string, userId: string, role: AppRole): Promise<AppMember>;
  /** Remove a member (owner only; cannot remove the owner). */
  removeMember(appId: string, userId: string): Promise<{ appId: string; userId: string; removed: boolean }>;

  // --- Publish to website ---
  /** Make the app's website public or password-protected (owner/admin). */
  publishToWeb(appId: string, opts: { visibility: 'public' | 'password'; password?: string; html?: string; aiAccess?: 'creator' | 'users' | 'open'; aiMonthlyBudget?: number; aiDailyPerPerson?: number; schemas?: PublishedCollection[] }): Promise<{ webPublished: boolean; webVisibility: 'public' | 'password'; slug?: string; siteUrl?: string; htmlHosted?: boolean }>;
  /** Take the app's website offline (owner/admin). */
  unpublishFromWeb(appId: string): Promise<{ webPublished: boolean }>;
  /** Exchange a site password for an app-scoped session token (null on failure). */
  unlockSite(appId: string, password: string): Promise<{ token: string } | null>;
  /** Fetch an app's definition, optionally carrying a site session token (for a
   *  password-protected published website). */
  getAppWithSite(appId: string, siteToken?: string): Promise<AppListing | null>;
}

export class BackendError extends Error {
  constructor(message: string, public code: 'not_found' | 'forbidden' | 'unauthorized' | 'validation') {
    super(message);
    this.name = 'BackendError';
  }
}
